import { FieldType } from '@ngx-formly/material';
import { Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { ENTER, COMMA, TAB } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';

import { ISnInputFormFieldConfig } from '../../interfaces/input-form-field-config.interface';
import { ISnInputTemplateOptions } from '../../interfaces/input-template-options.interface';

@Component({
  selector: 'sn-input-form-field',
  templateUrl: './input.form-field.html',
  styleUrls: ['./input.form-field.scss'],
})
export class SnInputFormField
  extends FieldType<ISnInputFormFieldConfig>
  implements OnInit, OnDestroy
{
  @ViewChild('chipInput') public chipInput: ElementRef;

  public readonly defaultOptions: ISnInputFormFieldConfig = {
    type: 'input',
    templateOptions: {
      appearance: 'fill',
      type: 'text',
      fetchFacets: false,
      responseListProp: 'results',
      responseListFilterProp: 'id',
    },
  };
  public filteredOptions$: Observable<any[]>;
  public items: any = [];
  public separatorKeysCodes: number[] = [ENTER, COMMA, TAB];
  public inputControl = new UntypedFormControl(null);
  public formArray: UntypedFormArray;
  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  get templateOptions(): ISnInputTemplateOptions {
    return this.field.templateOptions;
  }

  get type(): string {
    return this.to.type;
  }

  public ngOnInit(): void {
    if (this.templateOptions.type === 'autocomplete-text') {
      if (this.formControl.value) {
        this.items = this.formControl.value;
      }
      this.filteredOptions$ = this.inputControl.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((value) => {
          return this._fetchData({}, value || '');
        }),
      );

      this.formControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((values) => {
        this.formArray?.clear();
        this._changeDetectorRef.detectChanges();

        if (!values) {
          this.items = [];
          return;
        }
        values.forEach((value) => {
          this.formArray.push(new UntypedFormControl(value));
        });
      });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public addItem(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;
    if ((value || '').trim()) {
      this.items.push(value.trim());
    }

    if (input) {
      input.value = '';
      this.chipInput.nativeElement.value = '';
    }
    this.inputControl.setValue(null);
    this._updateControlValue();
  }

  public removeItem(item: any, index: number): void {
    this.items.splice(index, 1);
    this._updateControlValue();
  }

  public selected(event: MatAutocompleteSelectedEvent): void {
    this.items.push(event.option.value);
    this.inputControl.setValue(null);
    this.chipInput.nativeElement.value = '';
    this._updateControlValue();
  }

  private _updateControlValue(): void {
    const selectedOptions = [...this.items];
    this.formArray = new UntypedFormArray(selectedOptions.map((v) => new UntypedFormControl(v)));
    this.formControl.setValue(selectedOptions);
    if (Array.isArray(this.key)) {
      this.model[this.key[0]] = selectedOptions;
    } else {
      this.model[this.key] = selectedOptions;
    }
  }

  private _fetchData(params: ISnListQueryParameters = {}, value: any): Observable<any> {
    const responseListFilterProp = this.templateOptions.responseListFilterProp;
    const responseListProp = this.templateOptions.responseListProp;
    if (!this.to.fetchDataFn) {
      return of(null);
    }
    return this.to.fetchDataFn(params, this.model, this.field).pipe(
      map((response: any) =>
        response[responseListProp][responseListFilterProp].filter((option) => {
          return option.key.toLowerCase().indexOf(value.toLowerCase()) === 0;
        }),
      ),
    );
  }
}
